/**
 * Hook pour gérer les charges fixes d'une entreprise
 * Migré vers React Query (useQuery + useMutation)
 * @module hooks/useChargesFixes
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateTresorerie } from '@/lib/queryInvalidation';
import type {
  ChargeFix,
  CreateChargeFixData,
  UpdateChargeFixData,
  UseChargesFixesReturn,
} from '@/types/charges-fixes.types';

/**
 * Hook pour gérer les charges fixes d'une entreprise
 * @param entrepriseId - ID de l'entreprise
 */
export function useChargesFixes(entrepriseId: string | null): UseChargesFixesReturn {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const enabled = !!entrepriseId;

  // ============================================================================
  // QUERY
  // ============================================================================

  const { data: chargesFixes = [], isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.tresorerie.chargesFixes(entrepriseId!),
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('charges_fixes')
        .select('*')
        .eq('entreprise_id', entrepriseId!)
        .order('categorie', { ascending: true })
        .order('nom', { ascending: true });

      if (fetchError) throw fetchError;

      return (data || []) as ChargeFix[];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min (charges fixes changent rarement)
  });

  // ============================================================================
  // DERIVED
  // ============================================================================

  // Calculer le total mensuel (charges actives seulement)
  const totalMensuel = useMemo(() => {
    return chargesFixes
      .filter((c) => c.actif)
      .reduce((sum, c) => {
        switch (c.frequence) {
          case 'mensuel':
            return sum + c.montant_ht;
          case 'trimestriel':
            return sum + c.montant_ht / 3;
          case 'annuel':
            return sum + c.montant_ht / 12;
          default:
            return sum;
        }
      }, 0);
  }, [chargesFixes]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // Ajouter une charge fixe
  const ajouterMutation = useMutation({
    mutationFn: async (data: CreateChargeFixData) => {
      const { error: insertError } = await supabase
        .from('charges_fixes')
        .insert({
          entreprise_id: entrepriseId!,
          ...data,
        });

      if (insertError) throw insertError;
    },
    onSuccess: (_data, variables) => {
      toast({
        title: 'Charge fixe ajoutée',
        description: `"${variables.nom}" a été ajoutée avec succès`,
      });
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useChargesFixes] Erreur ajout:', err);
      toast({
        title: 'Erreur',
        description: "Impossible d'ajouter la charge fixe",
        variant: 'destructive',
      });
    },
  });

  // Ajouter plusieurs charges fixes en bulk
  const ajouterBulkMutation = useMutation({
    mutationFn: async (data: CreateChargeFixData[]) => {
      if (data.length === 0) return;

      const rows = data.map((item) => ({
        entreprise_id: entrepriseId!,
        ...item,
      }));

      const { error: insertError } = await supabase
        .from('charges_fixes')
        .insert(rows);

      if (insertError) throw insertError;
    },
    onSuccess: (_data, variables) => {
      toast({
        title: 'Charges fixes ajoutées',
        description: `${variables.length} charge(s) ajoutée(s) avec succès`,
      });
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useChargesFixes] Erreur ajout bulk:', err);
      toast({
        title: 'Erreur',
        description: "Impossible d'ajouter les charges fixes",
        variant: 'destructive',
      });
    },
  });

  // Modifier une charge fixe
  const modifierMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateChargeFixData }) => {
      const { error: updateError } = await supabase
        .from('charges_fixes')
        .update(data)
        .eq('id', id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast({
        title: 'Charge fixe modifiée',
        description: 'Modification enregistrée avec succès',
      });
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useChargesFixes] Erreur modification:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier la charge fixe',
        variant: 'destructive',
      });
    },
  });

  // Supprimer une charge fixe
  const supprimerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: deleteError } = await supabase
        .from('charges_fixes')
        .delete()
        .eq('id', id);

      if (deleteError) {
        // Si la charge a des références liées, on ne peut pas la supprimer
        if (deleteError.code === '23503') {
          toast({
            title: 'Suppression impossible',
            description: 'Cette charge fixe a des références liées.',
            variant: 'destructive',
          });
          return;
        }
        throw deleteError;
      }
    },
    onSuccess: () => {
      toast({
        title: 'Charge fixe supprimée',
        description: 'Suppression effectuée avec succès',
      });
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useChargesFixes] Erreur suppression:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la charge fixe',
        variant: 'destructive',
      });
    },
  });

  // Toggle actif/inactif (optimistic update)
  const toggleActifMutation = useMutation({
    mutationFn: async ({ id, actif }: { id: string; actif: boolean }) => {
      const { error: updateError } = await supabase
        .from('charges_fixes')
        .update({ actif })
        .eq('id', id);

      if (updateError) throw updateError;
      return { id, actif };
    },
    // Optimistic update
    onMutate: async ({ id, actif }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tresorerie.chargesFixes(entrepriseId!) });

      const previous = queryClient.getQueryData<ChargeFix[]>(queryKeys.tresorerie.chargesFixes(entrepriseId!));

      queryClient.setQueryData<ChargeFix[]>(
        queryKeys.tresorerie.chargesFixes(entrepriseId!),
        (old) => old?.map((c) => (c.id === id ? { ...c, actif } : c)) ?? []
      );

      return { previous };
    },
    onSuccess: (_data, { actif }) => {
      toast({
        title: actif ? 'Charge activée' : 'Charge désactivée',
        description: actif
          ? 'La charge sera incluse dans le prévisionnel'
          : 'La charge est exclue du prévisionnel',
      });
      // Invalider la trésorerie (prévisionnel dépend des charges actives)
      invalidateTresorerie(queryClient, entrepriseId!);
    },
    onError: (err, _vars, context) => {
      console.error('[useChargesFixes] Erreur toggle actif:', err);
      // Rollback optimistic update
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.tresorerie.chargesFixes(entrepriseId!), context.previous);
      }
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut',
        variant: 'destructive',
      });
    },
  });

  // ============================================================================
  // WRAPPERS (garder l'API identique)
  // ============================================================================

  const ajouterChargeFix = useCallback(
    async (data: CreateChargeFixData): Promise<void> => {
      await ajouterMutation.mutateAsync(data);
    },
    [ajouterMutation]
  );

  const ajouterChargesFixes = useCallback(
    async (data: CreateChargeFixData[]): Promise<boolean> => {
      if (!entrepriseId || data.length === 0) return false;
      try {
        await ajouterBulkMutation.mutateAsync(data);
        return true;
      } catch {
        return false;
      }
    },
    [entrepriseId, ajouterBulkMutation]
  );

  const modifierChargeFix = useCallback(
    async (id: string, data: UpdateChargeFixData): Promise<void> => {
      await modifierMutation.mutateAsync({ id, data });
    },
    [modifierMutation]
  );

  const supprimerChargeFix = useCallback(
    async (id: string): Promise<void> => {
      await supprimerMutation.mutateAsync(id);
    },
    [supprimerMutation]
  );

  const toggleActif = useCallback(
    async (id: string, actif: boolean): Promise<void> => {
      await toggleActifMutation.mutateAsync({ id, actif });
    },
    [toggleActifMutation]
  );

  return {
    chargesFixes,
    isLoading,
    error: error as Error | null,
    totalMensuel,
    ajouterChargeFix,
    ajouterChargesFixes,
    modifierChargeFix,
    supprimerChargeFix,
    toggleActif,
    refetch: async () => { await refetch(); },
  };
}
